import { useEffect } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';

import * as ROUTES from '#client/constants/routes';

/**
 * Custom hook to handle authentication checks and redirects
 *
 * Determines if authentication is required based on user mode and handles redirects
 * Anonymous and standalone modes don't require authentication
 * Starter and business modes require authentication
 *
 * @param {object} userMode - User mode object with mode and flags
 * @param {boolean} authenticated - Whether user is authenticated
 * @param {string} threadUuid - Current thread UUID
 * @param {string} redirectTo - Optional redirect URL from query params
 * @param {Function} onAuthRequired - Callback when auth is required
 * @param {Function} onAuthValid - Callback when auth is valid
 */
export function useThreadAuthentication(
  userMode,
  authenticated,
  threadUuid,
  redirectTo,
  onAuthRequired,
  onAuthValid
) {
  const { login } = useAuthentication();

  useEffect(() => {
    if (!userMode) return;

    // Anonymous and standalone modes don't require authentication
    // They can try to load threads, backend will validate access
    if (userMode.isAnonymous || userMode.isStandalone) {
      onAuthValid();
      return;
    }

    // Starter and business modes require authentication
    if (!authenticated) {
      // IMPORTANT: Always redirect to chat entry, not the thread URL
      // Reason: If user was viewing an anonymous thread and logs in,
      // returning to that thread would cause a 403/404 error because
      // the thread belongs to a different identity (visitor vs customer)
      const returnUrl = redirectTo || ROUTES.chatEntry;

      // Notify parent that auth is required
      onAuthRequired();

      // Perform the redirect
      login(returnUrl);
      return;
    }

    onAuthValid();
  }, [userMode, authenticated, login, redirectTo, threadUuid, onAuthRequired, onAuthValid]);
}
